import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  Box,
  Checkbox,
  CircularProgress,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import Drag from "../../../icons/Drag";
import {
  GridColDef,
  GridRenderCellParams,
  GridColumnHeaderParams,
  GridValidRowModel,
  GridCellParams,
} from "@mui/x-data-grid";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import {
  useGetConditionsFromExp,
  useGetItemsConstraints,
} from "../../../hooks/queries/useSpecQueries";
import { defaultQueryParams, QueryKey } from "../../../utils/api";
import client from "../../../api";
import {
  getAttributeConstraints,
  getParameterConstraints,
} from "./validationState";
import Chip from "../../../components/Chip";
import InputCell from "../../../components/forms/InputCell";
import DownloadIcon from "../../../icons/Download";
import FilterIcon from "../../../icons/Filter";
import DiffIcon from "../../../icons/Diff";
import GridViewFilterModal from "./GridViewFilterModal";
import GridContent from "./GridContent";
import {
  formatValueType,
  getEnumValuesFromConfig,
  isEnumDataType,
  isMultiSelectEnumDataType,
  isRangeValue,
  normalizeEnumValue,
  parseMultiEnumTokens,
  parseMultiEnumValue,
  serializeMultiEnum,
} from "../../utils/valueType";
import {
  buildDefaultGridViewVisibleColumns,
  reconcileGridViewFilters,
  useBulkEditStateStore,
  useGridViewFilterStateStore,
} from "./bulkEditState";
import { isItem, useSpecificationStateStore } from "./specificationState";
import { isConditionEditLocked } from "./statusState";
import {
  createConditionLink,
  areValuesEqual,
  isEditableGridField,
  STRUCTURAL_GRID_FIELDS,
  encodeColumnKey,
  parseColumnKey,
  resolveProcessByInstancePath,
  enumerateProcessInstances,
  instancePathOrdinals,
  getProcessTypeId,
  getItemTypeName,
  getItemPropertyNames,
  getProcessPropertyNames,
  humanizeFieldName,
  isFieldLocked,
  ColumnKeyParts,
} from "../../utils/conditions";
import ConditionNameLink from "./ConditionNameLink";
import Send from "../../../icons/Send";
import Pencil from "../../../icons/Pencil";
import Alert from "../../components/Alert";

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "100%",
  backgroundColor: "white",
}));

const HeaderContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  margin: "12px 12px 0px 12px",
  backgroundColor: "#f8f8f8",
  padding: 12,
});

// Helper function to get part number from an item or process
const getPartNumber = (
  obj: Item | ArenaOnlyItem | Process | undefined
): string => {
  if (!obj) return "";
  return obj.arena_part_number || "";
};

// Helper function to get template name from item or process
const getTemplateName = (
  obj: Item | ArenaOnlyItem | Process | undefined
): string => {
  if (!obj) return "";
  if ("base_template" in obj && obj.base_template) {
    return obj.base_template.name || "";
  }
  return "";
};

// Helper function to get value from item attributes or process parameters
const getValue = (
  source: Item | ArenaOnlyItem | Process | undefined,
  fieldName: string
): ValueType | null => {
  if (!source) return null;

  // Check if it's an item with attributes
  const item = source as Item | ArenaOnlyItem;
  if (item.attributes && item.attributes[fieldName] !== undefined) {
    const value = item.attributes[fieldName];

    return value as ValueType;
  }

  // Check if it's a process with parameters
  const process = source as Process;
  if (process.parameters && process.parameters[fieldName] !== undefined) {
    return process.parameters[fieldName];
  }

  return null;
};

// Helper function to safely format values for display
const formatValue = (value: ValueType | null | undefined): string => {
  return formatValueType(value ?? null);
};

const escapeCsvCell = (value: string): string => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
};

const valueToEditText = (value: ValueType | null | undefined): string => {
  if (value == null) {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  return "";
};

const getDifferenceVisibleColumns = (
  rows: GridValidRowModel[],
  allPropertyNames: string[]
): Set<string> => {
  const visibleColumns = new Set<string>();
  if (rows.length < 2) {
    return visibleColumns;
  }

  allPropertyNames.forEach((propertyName) => {
    const firstValue = rows[0][propertyName] as ValueType | null | undefined;
    let hasDifference = false;
    for (let i = 1; i < rows.length; i += 1) {
      if (
        !areValuesEqual(
          rows[i][propertyName] as ValueType | null | undefined,
          firstValue
        )
      ) {
        hasDifference = true;
        break;
      }
    }

    if (hasDifference) {
      visibleColumns.add(propertyName);
    }
  });

  return visibleColumns;
};

const serializeGridValue = (value: unknown): string => {
  if (value == null) {
    return "null";
  }

  if (typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(serializeGridValue).join(",")}]`;
  }

  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${serializeGridValue(record[key])}`)
    .join(",")}}`;
};

const getDifferenceFilterSignature = (
  rows: GridValidRowModel[],
  allPropertyNames: string[],
  visibleConditions: Set<number>,
  visibleColumns: Set<string>
): string => {
  const conditionPart = Array.from(visibleConditions).sort((a, b) => a - b);
  const columnPart = Array.from(visibleColumns).sort();
  const rowPart = rows
    .map((row) => ({
      id: row.id,
      values: allPropertyNames.map((propertyName) => [
        propertyName,
        serializeGridValue(row[propertyName]),
      ]),
    }))
    .sort((a, b) => String(a.id).localeCompare(String(b.id)));

  return JSON.stringify({ conditionPart, columnPart, rowPart });
};

type NestedFieldTarget =
  | {
      type: "attribute";
      path: string;
      propertyName: string;
    }
  | {
      type: "parameter";
      path: string;
      processPath: number[];
      propertyName: string;
    };

type ManualEditColumnMeta = {
  headerName: string;
  type: "string" | "number" | "boolean";
  kind: "text" | "number" | "boolean" | "enum_single" | "enum_multi" | "range";
  enumValues: string[];
};

type ManualEditInput = string | number | boolean | string[] | RangeValue | null;

type ManualEditCoercionResult =
  | {
      ok: true;
      value: ValueType;
    }
  | {
      ok: false;
      reason: string;
    };

const parseClipboardMatrix = (text: string): string[][] => {
  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const rawLines = normalized.split("\n");
  const lines =
    rawLines.length > 0 && rawLines[rawLines.length - 1] === ""
      ? rawLines.slice(0, -1)
      : rawLines;

  if (lines.length === 0) {
    return [];
  }

  const useComma =
    lines.every((line) => !line.includes("\t")) &&
    lines.some((line) => line.includes(","));

  return lines.map((line) => (useComma ? line.split(",") : line.split("\t")));
};

const coerceManualValue = (
  raw: ManualEditInput,
  columnMeta: ManualEditColumnMeta
): ManualEditCoercionResult => {
  if (columnMeta.kind === "number") {
    if (typeof raw === "number") {
      return Number.isFinite(raw)
        ? { ok: true, value: raw }
        : { ok: false, reason: "must be a valid number" };
    }

    const trimmed = String(raw ?? "").trim();
    if (trimmed === "") {
      return { ok: true, value: "" };
    }

    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) {
      return { ok: false, reason: "must be a valid number" };
    }

    return { ok: true, value: parsed };
  }

  if (columnMeta.kind === "boolean") {
    if (typeof raw === "boolean") {
      return { ok: true, value: raw };
    }

    const lowered = String(raw ?? "")
      .trim()
      .toLowerCase();
    if (["true", "1", "yes", "y"].includes(lowered)) {
      return { ok: true, value: true };
    }
    if (["false", "0", "no", "n"].includes(lowered)) {
      return { ok: true, value: false };
    }

    return {
      ok: false,
      reason: "must be true/false, yes/no, or 1/0",
    };
  }

  if (columnMeta.kind === "enum_single") {
    const rawText = String(raw ?? "");
    const trimmed = rawText.trim();
    if (trimmed === "") {
      if (columnMeta.enumValues.includes("")) {
        return { ok: true, value: "" };
      }
      return {
        ok: false,
        reason: "does not match allowed values",
      };
    }

    const normalized = normalizeEnumValue(rawText, columnMeta.enumValues);
    if (normalized == null) {
      return {
        ok: false,
        reason: "does not match allowed values",
      };
    }

    return { ok: true, value: normalized };
  }

  if (columnMeta.kind === "enum_multi") {
    const rawTokens = Array.isArray(raw)
      ? raw.map((token) => String(token).trim())
      : parseMultiEnumTokens(String(raw ?? ""));

    if (rawTokens.length === 0) {
      if (columnMeta.enumValues.includes("")) {
        return { ok: true, value: [] };
      }
      return {
        ok: false,
        reason: "does not match allowed values",
      };
    }

    const normalizedTokens: string[] = [];
    for (const token of rawTokens) {
      const normalized = normalizeEnumValue(token, columnMeta.enumValues);
      if (normalized == null) {
        return {
          ok: false,
          reason: "does not match allowed values",
        };
      }

      if (normalized && !normalizedTokens.includes(normalized)) {
        normalizedTokens.push(normalized);
      }
    }

    // Saved as string[] in enum_values order so the same selection is stable.
    return {
      ok: true,
      value: serializeMultiEnum(normalizedTokens, columnMeta.enumValues),
    };
  }

  if (columnMeta.kind === "range") {
    // Accept either a RangeValue object (from RangeEditor) or a string
    // like "5-10" / "5 - 10" (from paste). Both min and max are required.
    let minRaw: number | null = null;
    let maxRaw: number | null = null;

    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      const obj = raw as { min_value?: unknown; max_value?: unknown };
      if (typeof obj.min_value === "number") minRaw = obj.min_value;
      if (typeof obj.max_value === "number") maxRaw = obj.max_value;
    } else if (typeof raw === "string") {
      const text = raw.trim();
      if (text === "") {
        return { ok: false, reason: "min and max are required" };
      }
      // Split on hyphen with optional surrounding whitespace. Allow a
      // leading negative on either side (e.g. "-5--1").
      const match = text.match(
        /^\s*(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)\s*$/
      );
      if (!match) {
        return {
          ok: false,
          reason: "must be formatted as min-max (e.g. 5-10)",
        };
      }
      minRaw = Number(match[1]);
      maxRaw = Number(match[2]);
    } else if (typeof raw === "number") {
      // A bare number can't be a range.
      return { ok: false, reason: "must be formatted as min-max (e.g. 5-10)" };
    }

    if (minRaw === null || maxRaw === null) {
      return { ok: false, reason: "min and max are required" };
    }
    if (!Number.isFinite(minRaw) || !Number.isFinite(maxRaw)) {
      return { ok: false, reason: "min and max must be valid numbers" };
    }
    if (minRaw > maxRaw) {
      return { ok: false, reason: "min cannot exceed max" };
    }

    return {
      ok: true,
      value: { min_value: minRaw, max_value: maxRaw } as RangeValue,
    };
  }

  return { ok: true, value: String(raw ?? "") };
};

const coerceConditionName = (
  raw: ManualEditInput
): ManualEditCoercionResult => {
  const value = String(raw ?? "").trim();
  if (value === "") {
    return { ok: false, reason: "cannot be empty" };
  }

  return { ok: true, value };
};

// ---------------------------------------------------------------------------
// Column-key encoding
// ---------------------------------------------------------------------------
//
// To support conditions that contain multiple instances of the same
// process/item type at the same hierarchical position (e.g., a condition
// whose Anode has TWO `Anode Furnace` processes), each grid column is keyed
// by the FULL hierarchical position of an instance. The encode/parse codec
// and `resolveProcessByInstancePath` live in `utils/conditions.ts` so they
// can be reused by `GridViewFilterModal` and any future consumer.

// Resolve an item by its logical bomPath — a `>`-separated sequence of
// `<typeName>:<ordinal>` segments. The ordinal is the Nth occurrence of
// `typeName` among same-type siblings in the parent BOM list. Returns the
// resolved BOMItem along with the concrete raw-integer-index path
// (`>`-separated) that was actually walked, so callers can hand the raw
// path to downstream consumers (`applyPropertyEdit`, `parseBomPath`, etc.)
// that still expect raw BOM indices.
//
// An empty `bomPath` resolves to the condition root with `rawPath = ""`.
const resolveItemAtBomPath = (
  condition: ItemWithBOM,
  bomPath: string
): { item: ItemWithBOM; rawPath: string } | null => {
  if (!bomPath) {
    return { item: condition, rawPath: "" };
  }
  const segments = bomPath.split(">");
  let current: ItemWithBOM = condition;
  const rawIndices: number[] = [];

  for (const segment of segments) {
    const colonIdx = segment.lastIndexOf(":");
    if (colonIdx < 0) {
      return null;
    }
    const typeName = segment.slice(0, colonIdx);
    const ordinal = parseInt(segment.slice(colonIdx + 1), 10);
    if (Number.isNaN(ordinal) || !current.bom) {
      return null;
    }

    let matchCount = 0;
    let matchedIndex = -1;
    for (let i = 0; i < current.bom.length; i += 1) {
      if (getItemTypeName(current.bom[i].item) === typeName) {
        if (matchCount === ordinal) {
          matchedIndex = i;
          break;
        }
        matchCount += 1;
      }
    }
    if (matchedIndex < 0) {
      return null;
    }
    rawIndices.push(matchedIndex);
    current = current.bom[matchedIndex];
  }

  return { item: current, rawPath: rawIndices.join(">") };
};

// DragHandleCell component for proper hook usage
const DragHandleCell = ({ rowId }: { rowId: string | number }) => {
  const { attributes, listeners, setNodeRef } = useSortable({
    id: rowId,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        cursor: "grab",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
    >
      <Drag
        sx={{
          color: "#666",
          "&:hover": {
            color: "#1E5471",
          },
        }}
      />
    </div>
  );
};

// Inline editor for RangeValue cells. Renders two number inputs (min/max)
// side-by-side. Commits on blur of the wrapper (when focus leaves both
// inputs) or on Enter; cancels on Escape. Always commits both values as a
// `RangeValue` object; downstream `coerceManualValue` enforces the
// "min and max required, min <= max" rules. If the user opens the editor
// and blurs without changing anything, `commitManualEdit`'s
// `areValuesEqual` short-circuit (in GridView) prevents a spurious change.
const RangeEditor = ({
  initialValue,
  seedValue,
  onCommit,
  onCancel,
}: {
  initialValue: ValueType | null | undefined;
  seedValue?: string;
  onCommit: (nextValue: ManualEditInput) => void;
  onCancel: () => void;
}) => {
  const seedMin = isRangeValue(initialValue)
    ? Number.isFinite(initialValue.min_value)
      ? String(initialValue.min_value)
      : ""
    : "";
  const seedMax = isRangeValue(initialValue)
    ? Number.isFinite(initialValue.max_value)
      ? String(initialValue.max_value)
      : ""
    : "";

  // If the user double-clicked and started typing a number before the
  // editor mounted, MUI passes that keystroke as `seedValue`. Apply it to
  // the min input as a sensible default.
  const [minStr, setMinStr] = useState<string>(
    typeof seedValue === "string" && seedValue !== "" ? seedValue : seedMin
  );
  const [maxStr, setMaxStr] = useState<string>(seedMax);

  const handleCommit = () => {
    const trimmedMin = minStr.trim();
    const trimmedMax = maxStr.trim();

    // Both empty → treat as a no-op cancel so a stray double-click
    // doesn't wipe the original range. (commitManualEdit's
    // areValuesEqual would also short-circuit if the seeded values are
    // both empty, but cancelling explicitly avoids the error toast that
    // coerceManualValue would otherwise raise for "min and max are
    // required".)
    if (
      trimmedMin === "" &&
      trimmedMax === "" &&
      seedMin === "" &&
      seedMax === ""
    ) {
      onCancel();
      return;
    }

    // If both inputs match what was seeded, no real change — cancel to
    // skip coercion (which would also be a no-op via areValuesEqual).
    if (trimmedMin === seedMin && trimmedMax === seedMax) {
      onCancel();
      return;
    }

    const minNum = trimmedMin === "" ? Number.NaN : Number(trimmedMin);
    const maxNum = trimmedMax === "" ? Number.NaN : Number(trimmedMax);
    onCommit({
      min_value: minNum,
      max_value: maxNum,
    } as RangeValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === "Enter") {
      handleCommit();
    } else if (event.key === "Escape") {
      onCancel();
    }
  };

  // Wrapper-level blur: commit only when focus leaves BOTH inputs (i.e.,
  // moves outside the wrapper). Tabbing between min and max keeps focus
  // inside the wrapper and must not commit.
  const handleWrapperBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }
    handleCommit();
  };

  return (
    <Box
      onBlur={handleWrapperBlur}
      onClick={(event) => event.stopPropagation()}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        padding: "4px 6px",
        backgroundColor: "#fff",
      }}
    >
      <InputCell
        noTable
        autoFocus
        fullWidth
        type="text"
        value={minStr}
        placeholder="Min"
        onChange={(event) => setMinStr(event.target.value)}
        onKeyDown={handleKeyDown}
        inputProps={{
          inputMode: "decimal",
          pattern: "-?[0-9]*[.,]?[0-9]*",
          style: { textAlign: "left", paddingLeft: 0 },
        }}
        inputStyles={{
          width: "100%",
          height: "100%",
          fontSize: 13,
          padding: "4px 6px",
          backgroundColor: "#fff",
          border: "1px solid #ccc",
          borderRadius: 4,
        }}
      />
      <InputCell
        noTable
        fullWidth
        type="text"
        value={maxStr}
        placeholder="Max"
        onChange={(event) => setMaxStr(event.target.value)}
        onKeyDown={handleKeyDown}
        inputProps={{
          inputMode: "decimal",
          pattern: "-?[0-9]*[.,]?[0-9]*",
          style: { textAlign: "left", paddingLeft: 0 },
        }}
        inputStyles={{
          width: "100%",
          height: "100%",
          fontSize: 13,
          padding: "4px 6px",
          backgroundColor: "#fff",
          border: "1px solid #ccc",
          borderRadius: 4,
        }}
      />
    </Box>
  );
};

const InlineEditCell = ({
  initialValue,
  seedValue,
  columnMeta,
  onCommit,
  onCancel,
}: {
  initialValue: ValueType | null | undefined;
  seedValue?: string;
  columnMeta: ManualEditColumnMeta;
  onCommit: (nextValue: ManualEditInput) => void;
  onCancel: () => void;
}) => {
  const parseInitialMultiEnumValue = useCallback(
    (value: ValueType | null | undefined): string[] => {
      if (columnMeta.kind !== "enum_multi") {
        return [];
      }

      return parseMultiEnumValue(value, columnMeta.enumValues);
    },
    [columnMeta]
  );

  const [value, setValue] = useState<ManualEditInput>(() => {
    if (columnMeta.kind === "enum_multi") {
      return parseInitialMultiEnumValue(initialValue);
    }

    if (columnMeta.kind === "boolean") {
      if (typeof initialValue === "boolean") {
        return initialValue;
      }
      return false;
    }

    if (columnMeta.kind === "number") {
      if (typeof seedValue === "string") {
        return seedValue;
      }
      if (typeof initialValue === "number") {
        return initialValue;
      }
      if (typeof initialValue === "string") {
        return initialValue;
      }
      return "";
    }

    if (columnMeta.kind === "enum_single") {
      if (typeof initialValue === "string") {
        const normalized = normalizeEnumValue(
          initialValue,
          columnMeta.enumValues
        );
        return normalized ?? "";
      }
      return "";
    }

    if (typeof seedValue === "string") {
      return seedValue;
    }
    return valueToEditText(initialValue);
  });

  useEffect(() => {
    if (columnMeta.kind === "enum_multi") {
      setValue(parseInitialMultiEnumValue(initialValue));
      return;
    }

    if (columnMeta.kind === "boolean") {
      setValue(typeof initialValue === "boolean" ? initialValue : false);
      return;
    }

    if (columnMeta.kind === "number") {
      if (typeof initialValue === "number") {
        setValue(initialValue);
      } else if (typeof initialValue === "string") {
        setValue(initialValue);
      } else {
        setValue("");
      }
      return;
    }

    if (columnMeta.kind === "enum_single") {
      if (typeof initialValue === "string") {
        const normalized = normalizeEnumValue(
          initialValue,
          columnMeta.enumValues
        );
        setValue(normalized ?? "");
      } else {
        setValue("");
      }
      return;
    }

    setValue(valueToEditText(initialValue));
  }, [columnMeta, initialValue, parseInitialMultiEnumValue]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === "Enter") {
      onCommit(value);
    }
    if (event.key === "Escape") {
      onCancel();
    }
  };

  // While a Select's dropdown menu is open, MUI portals the menu outside
  // the wrapper Box, causing the wrapper's `onBlur` to fire with a
  // `relatedTarget` that is not contained in the wrapper. That would
  // immediately cancel edit mode the instant the user opened the dropdown.
  // Track open state and suppress wrapper blur while open; rely on the
  // Select's own `onClose` callback as the canonical dismiss path.
  const menuOpenRef = useRef(false);

  const handleSelectWrapperBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (menuOpenRef.current) {
      return;
    }
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      onCancel();
    }
  };

  if (columnMeta.kind === "enum_single") {
    return (
      <Box
        onBlur={handleSelectWrapperBlur}
        sx={{ width: "100%", height: "100%" }}
      >
        <Select
          autoFocus
          value={typeof value === "string" ? value : ""}
          onChange={(event) => {
            const next = String(event.target.value);
            setValue(next);
            onCommit(next);
          }}
          onOpen={() => {
            menuOpenRef.current = true;
          }}
          onClose={() => {
            menuOpenRef.current = false;
            onCancel();
          }}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={handleKeyDown}
          fullWidth
          size="small"
          sx={{ fontSize: 13, height: "100%" }}
        >
          {columnMeta.enumValues.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }

  if (columnMeta.kind === "enum_multi") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <Box
        onBlur={handleSelectWrapperBlur}
        sx={{ width: "100%", height: "100%" }}
      >
        <Select
          multiple
          autoFocus
          value={selected}
          onChange={(event) => {
            const next = event.target.value;
            const nextArray = Array.isArray(next)
              ? next.map((v) => String(v))
              : [];
            setValue(nextArray);
            onCommit(nextArray);
          }}
          onOpen={() => {
            menuOpenRef.current = true;
          }}
          onClose={() => {
            menuOpenRef.current = false;
            onCancel();
          }}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={handleKeyDown}
          renderValue={(selectedValues) =>
            (selectedValues as string[]).join(", ")
          }
          fullWidth
          size="small"
          sx={{ fontSize: 13, height: "100%" }}
        >
          {columnMeta.enumValues.map((option) => (
            <MenuItem key={option} value={option}>
              <Checkbox checked={selected.includes(option)} size="small" />
              <ListItemText primary={option} />
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }

  if (columnMeta.kind === "boolean") {
    return (
      <Box
        onBlur={handleSelectWrapperBlur}
        sx={{ width: "100%", height: "100%" }}
      >
        <Select
          autoFocus
          value={value === true ? "true" : "false"}
          onChange={(event) => {
            const next = event.target.value === "true";
            setValue(next);
            onCommit(next);
          }}
          onOpen={() => {
            menuOpenRef.current = true;
          }}
          onClose={() => {
            menuOpenRef.current = false;
            onCancel();
          }}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={handleKeyDown}
          fullWidth
          size="small"
          sx={{ fontSize: 13, height: "100%" }}
        >
          <MenuItem value="true">True</MenuItem>
          <MenuItem value="false">False</MenuItem>
        </Select>
      </Box>
    );
  }

  if (columnMeta.kind === "range") {
    return (
      <RangeEditor
        initialValue={initialValue}
        seedValue={seedValue}
        onCommit={onCommit}
        onCancel={onCancel}
      />
    );
  }

  return (
    <InputCell
      noTable
      autoFocus
      fullWidth
      type="text"
      value={value as string | number}
      onChange={(event) => {
        const nextRaw = event.target.value;
        setValue(nextRaw);
      }}
      onBlur={() => onCommit(value)}
      onClick={(event) => event.stopPropagation()}
      style={{ width: "100%", height: "100%" }}
      inputProps={{
        style: {
          textAlign: "left",
          paddingLeft: 0,
        },
        // Use inputMode + pattern instead of type="number" to keep the
        // numeric keypad on mobile while avoiding the native wheel
        // increment/decrement that would otherwise mutate the value when
        // the user scrolls the grid with this cell focused.
        ...(columnMeta.kind === "number"
          ? { inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }
          : {}),
      }}
      onKeyDown={(event) => {
        handleKeyDown(event);
      }}
      inputStyles={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        fontSize: 13,
        padding: "8px 10px",
        backgroundColor: "#fff",
        textAlign: "left",
        border: "none",
      }}
    />
  );
};

type GridViewProps = {
  /**
   * Pre-navigation guard supplied by the parent (Specification.tsx) so that
   * clicking a condition link with WIP bulk edits prompts the shared
   * "you have unsaved changes" confirmation. Should return true to proceed
   * with navigation, false to abort.
   */
  checkUnsavedChangesBeforeNavigation: () => boolean;
  /**
   * Set of cell_condition_ids whose condition is already part of a
   * submitted (or in-modification) work order group. Rows whose
   * conditionId is in this set are locked from inline edits, paste, and
   * any other mutation surface in GridView. Derived in Specification.tsx
   * so GridView and StatusView agree on what's "submitted".
   */
  submittedConditionIds: Set<number>;
};

const GridView = ({
  checkUnsavedChangesBeforeNavigation,
  submittedConditionIds,
}: GridViewProps) => {
  const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set());
  const [visibleConditions, setVisibleConditions] = useState<Set<number>>(
    new Set()
  );
  const [differenceFilterSignature, setDifferenceFilterSignature] = useState<
    string | null
  >(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string | number | null>(null);
  const [activeCell, setActiveCell] = useState<{
    rowId: string | number;
    field: string;
  } | null>(null);
  const [recentlyEditedCells, setRecentlyEditedCells] = useState<Set<string>>(
    new Set()
  );
  const [pasteErrorCells, setPasteErrorCells] = useState<Set<string>>(
    new Set()
  );
  const [editingCell, setEditingCell] = useState<{
    rowId: string | number;
    field: string;
    initialValue?: string;
  } | null>(null);
  const [columnMetaByField, setColumnMetaByField] = useState<
    Record<string, ManualEditColumnMeta>
  >({});
  const [copyToastMessage, setCopyToastMessage] = useState<string | null>(null);
  const columnMetaByFieldRef = useRef<Record<string, ManualEditColumnMeta>>({});
  const commitManualEditRef = useRef<
    (
      rowId: string | number,
      field: string,
      rawValue: ManualEditInput,
      rowSnapshot?: GridValidRowModel
    ) => boolean
  >(() => false);
  const gridApiRef = useRef<any>(null);
  const initializedBaselineRef = useRef<boolean>(false);
  const initializeBulkBaseline = useBulkEditStateStore(
    (state) => state.initializeBaseline
  );
  const trackBulkCellChange = useBulkEditStateStore(
    (state) => state.trackCellChange
  );
  const setErrorMessage = useSpecificationStateStore(
    (state) => state.setErrorMessage
  );
  // The work-order group currently being modified (set by StatusView's
  // "Modify" action). When set, GridView must mirror BOMView: only the
  // conditions belonging to this group are editable; all other submitted
  // conditions stay locked.
  const activeModifyConditionId = useSpecificationStateStore(
    (state) => state.activeModifyConditionId
  );
  const { exp_id = "" } = useParams();
  const savedFilters = useGridViewFilterStateStore(
    (state) => state.filtersByExpId[exp_id]
  );
  const persistFilters = useGridViewFilterStateStore(
    (state) => state.setFilters
  );
  const clearPersistedFilters = useGridViewFilterStateStore(
    (state) => state.clearFilters
  );
  const { data: conditions, isSuccess: isGetConditionsSuccess } =
    useGetConditionsFromExp({
      enabled: true,
    });

  // Router helpers for navigating from a row to the BOM edit view for that
  // condition. We stash the current pathname/searchParams and the
  // unsaved-changes guard in a ref because both `useSearchParams` and the
  // guard's callback get fresh identities on every render; depending on
  // them from the columns useMemo would invalidate the DataGrid's column
  // array each render and trigger an infinite update loop.
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const linkContextRef = useRef({
    pathname: location.pathname,
    searchParams,
    checkUnsavedChangesBeforeNavigation,
  });
  linkContextRef.current = {
    pathname: location.pathname,
    searchParams,
    checkUnsavedChangesBeforeNavigation,
  };

  // Fetch constraint validations for every condition's item. Reuses the same
  // per-uuid React Query cache that StatusView populates, so navigating
  // between StatusView and GridView avoids refetching.
  const conditionItemUuids = useMemo(
    () =>
      (conditions ?? [])
        .map((c) => (isItem(c.item) ? c.item.uuid : null))
        .filter((uuid): uuid is string => uuid !== null),
    [conditions]
  );
  // Fetch constraint validations for every condition's item. We use
  // `useQueries` directly (rather than calling `useItemValidation` in a
  // loop) so the same per-uuid React Query cache populated by
  // Specification.tsx and StatusView is reused without refetching.
  const validationQueries = useQueries({
    queries: conditionItemUuids.map((uuid) => ({
      queryKey: [QueryKey.Items, uuid, "validation"],
      queryFn: async (): Promise<TemplateValidationResult> => {
        return await client.get(`formspec/items/${uuid}/validation`);
      },
      enabled: !!uuid,
      ...defaultQueryParams,
    })),
  });

  // Stable signature so downstream memos do not invalidate every render.
  const validationsSignature = validationQueries
    .map((q, i) => {
      const uuid = conditionItemUuids[i] ?? "";
      const v = q.data;
      return `${uuid}:${v ? v.violations?.length ?? 0 : "_"}`;
    })
    .join("|");

  const conditionValidations = useMemo(
    () =>
      validationQueries
        .map((q) => q.data)
        .filter((v): v is TemplateValidationResult => !!v),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validationsSignature]
  );

  const isConditionValidationsLoading = validationQueries.some(
    (q) => q.isLoading
  );

  // Fetch template constraints (attribute_lock / parameter_lock / etc.) for
  // every condition's root item. Reuses the same per-uuid React Query cache
  // populated by Specification.tsx so navigating between views avoids
  // refetching. We use `queries[].data` indexed positionally to preserve the
  // mapping from each condition's root uuid to its constraints — including
  // the "still loading" state, which we need for pessimistic locking.
  const { queries: conditionConstraintQueries } =
    useGetItemsConstraints(conditionItemUuids);

  // Stable signature string for the per-query (uuid, isFetched, hasData)
  // tuple. Extracted out of the `useMemo` deps below so the lint rule can
  // statically inspect the dependency array (it complains about complex
  // expressions inline).
  const constraintsQuerySignature = conditionConstraintQueries
    .map(
      (q, i) =>
        `${conditionItemUuids[i] ?? ""}:${q?.isFetched ? "y" : "n"}:${
          q?.data ? "d" : "_"
        }`
    )
    .join("|");

  // Map root-item-uuid -> { constraints, isLoaded }. Pessimistic locking:
  // while a condition's constraints are still loading we treat every cell as
  // locked so users cannot commit edits that may turn out to violate a lock
  // constraint. The map is rebuilt only when query data or its loaded state
  // actually changes, to keep `baseRows` memoization stable.
  const constraintsByRootUuid = useMemo(() => {
    const map = new Map<
      string,
      { constraints: ConstraintsResponse | null; isLoaded: boolean }
    >();
    conditionItemUuids.forEach((uuid, index) => {
      const query = conditionConstraintQueries[index];
      map.set(uuid, {
        constraints: (query?.data as ConstraintsResponse | undefined) ?? null,
        isLoaded: Boolean(query?.isFetched),
      });
    });
    return map;
    // Re-derive only when the resolved/loaded state of any query changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionItemUuids, constraintsQuerySignature]);

  // Map each condition's `cell_condition_id` to whether its item has any
  // server-reported constraint violations. Only populated once all per-uuid
  // queries have resolved, so the warning icon does not flicker in.
  const violationsByConditionId = useMemo(() => {
    const map = new Map<number, boolean>();
    if (isConditionValidationsLoading || !conditions) {
      return map;
    }
    const validationByUuid = new Map<string, TemplateValidationResult>();
    conditionValidations.forEach((validation, index) => {
      const uuid = conditionItemUuids[index];
      if (uuid && validation) {
        validationByUuid.set(uuid, validation);
      }
    });
    conditions.forEach((c) => {
      if (!isItem(c.item)) return;
      const validation = validationByUuid.get(c.item.uuid);
      const hasViolations = !!validation?.violations?.length;
      map.set(c.cell_condition_id, hasViolations);
    });
    return map;
  }, [
    conditions,
    conditionValidations,
    conditionItemUuids,
    isConditionValidationsLoading,
  ]);

  // Setup dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Map of item type UUIDs/names to display names
  const itemTypeDisplayNames = useMemo(() => {
    if (!conditions) return new Map<string, string>();

    const displayNames = new Map<string, string>();

    const collectItemTypeNames = (item: Item | ArenaOnlyItem | undefined) => {
      if (!item) return;
      const itemTypeName = getItemTypeName(item);
      if (!displayNames.has(itemTypeName)) {
        displayNames.set(itemTypeName, itemTypeName);
      }
    };

    const collectFromBOM = (bom: BOMItem[] | undefined) => {
      if (!bom) return;
      bom.forEach((bomItem) => {
        collectItemTypeNames(bomItem.item);
        if (bomItem.bom && bomItem.bom.length > 0) {
          collectFromBOM(bomItem.bom);
        }
      });
    };

    conditions.forEach((condition) => {
      collectItemTypeNames(condition.item);
      collectFromBOM(condition.bom);
    });

    return displayNames;
  }, [conditions]);

  // Map of process type UUIDs/names to display names
  const processTypeDisplayNames = useMemo(() => {
    if (!conditions) return new Map<string, string>();

    const displayNames = new Map<string, string>();

    const collectProcessTypeNames = (process: Process | undefined) => {
      if (!process) return;
      const processTypeId = getProcessTypeId(process);
      const processTypeName = process.process_type?.name || "Unknown";
      if (!displayNames.has(processTypeId)) {
        displayNames.set(processTypeId, processTypeName);
      }
    };

    const collectFromProcessSteps = (
      processWithSteps: ProcessWithSteps[] | undefined
    ) => {
      if (!processWithSteps) return;
      processWithSteps.forEach((pws) => {
        collectProcessTypeNames(pws.process);
        if (pws.steps && pws.steps.length > 0) {
          collectFromProcessSteps(pws.steps);
        }
      });
    };

    const collectFromBOMProcesses = (bom: BOMItem[] | undefined) => {
      if (!bom) return;
      bom.forEach((bomItem) => {
        // Collect from this BOM item's processes
        collectFromProcessSteps(bomItem.processes);

        // Recursively collect from nested BOM items
        if (bomItem.bom && bomItem.bom.length > 0) {
          collectFromBOMProcesses(bomItem.bom);
        }
      });
    };

    conditions.forEach((condition) => {
      collectFromProcessSteps(condition.processes);
      collectFromBOMProcesses(condition.bom);
    });

    return displayNames;
  }, [conditions]);

  // Single pass over the conditions tree that produces every piece of
  // grid-shape state we need downstream:
  //
  //   - propertyNames:         deduped sorted column keys for `allPropertyNames`
  //   - processInstances:      `${bomPath}|P:${typeIdentity}` ->
  //                            set of instancePaths seen at that location
  //   - itemSiblingCounts:     `${parentBomPath}|I:${typeIdentity}` ->
  //                            max same-type sibling count across conditions
  //
  // `bomPath` is built from `<typeName>:<ordinal>` segments rather than raw
  // BOM indices so two conditions whose BOMs reorder same-type siblings
  // (e.g. different Anode Blend Materials at different `bom[i]` indices)
  // still encode to the same logical column key. The trailing segment of
  // `parts.bomPath` is also the per-instance ordinal used by
  // `buildColumnDisplayInfo` for the `#N` disambiguator.
  const gridIndex = useMemo(() => {
    const propertySet = new Set<string>();
    const processInstances = new Map<string, Set<string>>();
    const itemSiblingCounts = new Map<string, number>();

    const recordProcessInstance = (
      bomPath: string,
      typeIdentity: string,
      instancePath: string
    ) => {
      const key = `${bomPath}|P:${typeIdentity}`;
      let set = processInstances.get(key);
      if (!set) {
        set = new Set();
        processInstances.set(key, set);
      }
      set.add(instancePath);
    };

    const collectItemFieldKeys = (
      item: Item | ArenaOnlyItem | undefined,
      bomPath: string
    ) => {
      if (!item) return;
      const typeIdentity = getItemTypeName(item);
      const baseParts: Omit<ColumnKeyParts, "fieldName"> = {
        bomPath,
        typeKind: "I",
        typeIdentity,
        instancePath: "0",
      };

      getItemPropertyNames(item).forEach((attrName) => {
        propertySet.add(encodeColumnKey({ ...baseParts, fieldName: attrName }));
      });
      propertySet.add(
        encodeColumnKey({ ...baseParts, fieldName: "_part_number" })
      );
      propertySet.add(
        encodeColumnKey({ ...baseParts, fieldName: "_template_name" })
      );
    };

    // Instance paths come from the shared codec in utils/conditions so they
    // round-trip through `resolveProcessByInstancePath`.
    const collectProcessFieldKeys = (
      processWithSteps: ProcessWithSteps[] | undefined,
      bomPath: string
    ) => {
      enumerateProcessInstances(processWithSteps).forEach(
        ({ entry: pws, typeIdentity, instancePath }) => {
          recordProcessInstance(bomPath, typeIdentity, instancePath);

          const baseParts: Omit<ColumnKeyParts, "fieldName"> = {
            bomPath,
            typeKind: "P",
            typeIdentity,
            instancePath,
          };

          getProcessPropertyNames(pws.process).forEach((paramName) => {
            propertySet.add(
              encodeColumnKey({ ...baseParts, fieldName: paramName })
            );
          });
          propertySet.add(
            encodeColumnKey({ ...baseParts, fieldName: "_part_number" })
          );
          propertySet.add(
            encodeColumnKey({ ...baseParts, fieldName: "_template_name" })
          );
        }
      );
    };

    const traverseBOM = (bom: BOMItem[] | undefined, parentBomPath: string) => {
      if (!bom) return;

      // Tally same-type siblings at this BOM level so we can update
      // itemSiblingCounts and assign per-condition ordinals to each item
      // column key in one shot.
      const siblingTally = new Map<string, number>();
      bom.forEach((bomItem) => {
        const typeName = getItemTypeName(bomItem.item);
        siblingTally.set(typeName, (siblingTally.get(typeName) ?? 0) + 1);
      });
      siblingTally.forEach((count, typeName) => {
        const key = `${parentBomPath}|I:${typeName}`;
        if ((itemSiblingCounts.get(key) ?? 0) < count) {
          itemSiblingCounts.set(key, count);
        }
      });

      // Per-condition ordinal counter for each type at this level. The
      // childPath uses `<typeName>:<ordinal>` segments rather than raw BOM
      // indices so two conditions with the same logical "first GDE under
      // Cathode" align to the same encoded column key even if the GDE sits
      // at a different raw `bom[i]` index in each condition. The ordinal is
      // also the per-condition `#N` disambiguator (when sibling count > 1).
      const seenSoFar = new Map<string, number>();
      for (let i = 0; i < bom.length; i += 1) {
        const bomItem = bom[i];
        const typeName = getItemTypeName(bomItem.item);
        const ordinal = seenSoFar.get(typeName) ?? 0;
        seenSoFar.set(typeName, ordinal + 1);

        const segment = `${typeName}:${ordinal}`;
        const childPath = parentBomPath
          ? `${parentBomPath}>${segment}`
          : segment;

        collectItemFieldKeys(bomItem.item, childPath);
        collectProcessFieldKeys(bomItem.processes, childPath);
        if (bomItem.bom && bomItem.bom.length > 0) {
          traverseBOM(bomItem.bom, childPath);
        }
      }
    };

    (conditions || []).forEach((condition) => {
      collectItemFieldKeys(condition.item, "");
      collectProcessFieldKeys(condition.processes, "");
      traverseBOM(condition.bom, "");
    });

    return {
      propertyNames: Array.from(propertySet).sort(),
      processInstances,
      itemSiblingCounts,
    };
  }, [conditions]);

  const allPropertyNames = gridIndex.propertyNames;

  // Direct path-based lookup. The column field encodes (bomPath, type kind,
  // type identity, instance ordinal, fieldName), so we resolve the exact
  // instance the column represents instead of searching by type and
  // returning the first match (which silently flattened multi-instance
  // siblings into a single cell). Returns null when the condition has no
  // such instance — the cell is rendered empty and read-only via the
  // `_has_*` flag set in `baseRows`.
  // Unified resolver for a column key against a condition. The grid render
  // and edit paths used to call multiple near-duplicate helpers
  // (`getNestedFieldValue`, `resolveNestedFieldTarget`), each parsing the
  // same key and walking the same tree. This single function returns
  // everything any caller needs in one walk.
  //
  // Returns `null` when the column key is malformed. When the condition
  // does not contain the referenced instance, returns a result with
  // `exists: false` so callers can distinguish "no instance" from "no
  // value". `target` is `null` for the synthetic `_part_number` /
  // `_template_name` columns (read-only) or when the resolved instance does
  // not actually expose the field.
  const resolveNestedField = useCallback(
    (
      condition: ItemWithBOM,
      fieldPath: string
    ): {
      parts: ColumnKeyParts;
      exists: boolean;
      value: ValueType | null;
      target: NestedFieldTarget | null;
      // UUID of the resolved BOM item (only set when `parts.typeKind === "I"`
      // and the item is a real Item with a uuid; null for ArenaOnlyItem / no
      // match). Used by `baseRows` to look up `attribute_lock` constraints.
      resolvedItemUuid: string | null;
      // UUID of the resolved process (only set when `parts.typeKind === "P"`
      // and a process matches the encoded instance path). Used by `baseRows`
      // to look up `parameter_lock` constraints.
      resolvedProcessUuid: string | null;
    } | null => {
      const parts = parseColumnKey(fieldPath);
      if (!parts) return null;

      const isSynthetic =
        parts.fieldName === "_part_number" ||
        parts.fieldName === "_template_name";

      const buildSyntheticValue = (
        source: Item | ArenaOnlyItem | Process | undefined
      ): ValueType | null => {
        if (parts.fieldName === "_part_number") return getPartNumber(source);
        if (parts.fieldName === "_template_name")
          return getTemplateName(source);
        return null;
      };

      if (parts.typeKind === "I") {
        const resolved = resolveItemAtBomPath(condition, parts.bomPath);
        if (!resolved || !resolved.item.item) {
          return {
            parts,
            exists: false,
            value: null,
            target: null,
            resolvedItemUuid: null,
            resolvedProcessUuid: null,
          };
        }
        const item = resolved.item;
        if (getItemTypeName(item.item) !== parts.typeIdentity) {
          return {
            parts,
            exists: false,
            value: null,
            target: null,
            resolvedItemUuid: null,
            resolvedProcessUuid: null,
          };
        }

        // ArenaOnlyItem has no uuid; only real Items can be the subject of a
        // template constraint.
        const resolvedItemUuid = isItem(item.item) ? item.item.uuid : null;

        if (isSynthetic) {
          const value = buildSyntheticValue(item.item);
          return {
            parts,
            exists: value !== "",
            value,
            target: null,
            resolvedItemUuid,
            resolvedProcessUuid: null,
          };
        }

        // A null value still counts as present here; `baseRows` then hides
        // null cells that are locked and leaves the rest editable.
        const exists = getItemPropertyNames(item.item).includes(
          parts.fieldName
        );
        return {
          parts,
          exists,
          value: exists ? getValue(item.item, parts.fieldName) : null,
          target: exists
            ? {
                type: "attribute",
                // Translate the logical bomPath into the raw integer-index
                // path for THIS condition so `applyPropertyEdit` writes to
                // the right `bom[i]` slot.
                path: resolved.rawPath,
                propertyName: parts.fieldName,
              }
            : null,
          resolvedItemUuid,
          resolvedProcessUuid: null,
        };
      }

      // Process column.
      const resolvedOwner = resolveItemAtBomPath(condition, parts.bomPath);
      if (!resolvedOwner) {
        return {
          parts,
          exists: false,
          value: null,
          target: null,
          resolvedItemUuid: null,
          resolvedProcessUuid: null,
        };
      }
      const owningItem = resolvedOwner.item;
      const resolved = resolveProcessByInstancePath(
        owningItem.processes,
        parts.typeIdentity,
        parts.instancePath
      );
      if (!resolved) {
        return {
          parts,
          exists: false,
          value: null,
          target: null,
          resolvedItemUuid: null,
          resolvedProcessUuid: null,
        };
      }

      const resolvedProcessUuid = resolved.entry.process.uuid ?? null;

      if (isSynthetic) {
        const value = buildSyntheticValue(resolved.entry.process);
        return {
          parts,
          exists: value !== "",
          value,
          target: null,
          resolvedItemUuid: null,
          resolvedProcessUuid,
        };
      }

      const exists = getProcessPropertyNames(resolved.entry.process).includes(
        parts.fieldName
      );
      return {
        parts,
        exists,
        value: exists
          ? getValue(resolved.entry.process, parts.fieldName)
          : null,
        target: exists
          ? {
              type: "parameter",
              path: resolvedOwner.rawPath,
              processPath: resolved.processPath,
              propertyName: parts.fieldName,
            }
          : null,
        resolvedItemUuid: null,
        resolvedProcessUuid,
      };
      // `resolveNestedField` only closes over module-level imports and
      // utilities (no component state / props), so an empty dep array is
      // correct and gives it a stable identity for downstream memos.
    },
    []
  );

  // Thin compatibility wrappers — preserved so the call sites elsewhere in
  // this file stay readable. Each delegates to `resolveNestedField`. They
  // are `useCallback`-wrapped so they have stable identities and can be
  // safely listed in the dep arrays of the consuming `useMemo` /
  // `useCallback` hooks below.
  const getNestedFieldValue = useCallback(
    (condition: ItemWithBOM, fieldPath: string): ValueType | null => {
      const result = resolveNestedField(condition, fieldPath);
      return result?.value ?? null;
    },
    [resolveNestedField]
  );

  const resolveNestedFieldTarget = useCallback(
    (condition: ItemWithBOM, fieldPath: string): NestedFieldTarget | null => {
      const result = resolveNestedField(condition, fieldPath);
      return result?.target ?? null;
    },
    [resolveNestedField]
  );

  // Transform conditions data into grid rows
  const baseRows: GridValidRowModel[] = useMemo(() => {
    if (!conditions) return [];

    return conditions.map((condition, index) => {
      const row: GridValidRowModel = {
        id: condition.cell_condition_id || index + 1,
        conditionId: condition.cell_condition_id || index + 1,
        conditionName: condition.name || "",
        // Store the original condition for reference
        _condition: condition,
      };

      // Resolve the constraints payload for this condition's root item.
      // Pessimistic locking: if the constraints query has not yet resolved
      // for this root, every cell on this row is treated as locked. Once
      // the query settles (with data or null), `isLoaded` flips true and
      // per-field locks become accurate.
      const rootUuid = isItem(condition.item) ? condition.item.uuid : null;
      const constraintsEntry = rootUuid
        ? constraintsByRootUuid.get(rootUuid)
        : undefined;
      const constraintsLoaded = constraintsEntry?.isLoaded ?? false;
      const conditionConstraints = constraintsEntry?.constraints ?? null;

      // Add all attributes and parameters as fields. One resolver call per
      // cell (down from the previous two: value + has). Each cell also
      // records a `_locked_*` flag that mirrors the `isLocked` rule used by
      // `AttributeInput`/`ParameterInput` in TrackingInputs.tsx: a cell is
      // locked iff the matching template constraint is `attribute_lock` or
      // `parameter_lock`.
      allPropertyNames.forEach((propName) => {
        const result = resolveNestedField(condition, propName);
        row[propName] = result?.value ?? null;

        let isLocked = false;
        if (!constraintsLoaded) {
          // Pessimistic default while constraints are in flight.
          isLocked = true;
        } else if (result && conditionConstraints) {
          if (
            result.parts.typeKind === "I" &&
            result.resolvedItemUuid &&
            !result.parts.fieldName.startsWith("_")
          ) {
            isLocked = isFieldLocked(
              getAttributeConstraints(
                conditionConstraints,
                result.resolvedItemUuid,
                result.parts.fieldName
              )
            );
          } else if (
            result.parts.typeKind === "P" &&
            result.resolvedProcessUuid &&
            !result.parts.fieldName.startsWith("_")
          ) {
            isLocked = isFieldLocked(
              getParameterConstraints(
                conditionConstraints,
                result.resolvedProcessUuid,
                result.parts.fieldName
              )
            );
          }
        }
        row[`_locked_${propName}`] = isLocked;

        // Null + locked → display as absent (italic / non-editable, no value).
        // Null + editable → display as present so the user can fill it in.
        // Non-null is always present regardless of lock (locked cells with
        // values still render their value, just non-editable).
        const exists = result?.exists ?? false;
        const value = result?.value;
        if (exists && value == null && isLocked) {
          row[`_has_${propName}`] = false;
        } else {
          row[`_has_${propName}`] = exists;
        }
      });

      return row;
    });
  }, [conditions, allPropertyNames, constraintsByRootUuid, resolveNestedField]);

  const [rows, setRows] = useState<GridValidRowModel[]>(baseRows);
  const [overId, setOverId] = useState<string | number | null>(null);
  const [sortModel, setSortModel] = useState<any[]>([]);
  const cellChangesByKey = useBulkEditStateStore(
    (state) => state.cellChangesByKey
  );
  const pendingSaveConditionIds = useBulkEditStateStore(
    (state) => state.pendingSaveConditionIds
  );
  const pendingSaveConditionIdSet = useMemo(
    () => new Set(pendingSaveConditionIds),
    [pendingSaveConditionIds]
  );

  // Compute all condition IDs for filter detection
  const allConditionIds = useMemo(() => {
    return new Set(
      conditions?.map((c) => c.cell_condition_id).filter((id) => id != null) ||
        []
    );
  }, [conditions]);

  // Detect if any filters are applied
  const hasFiltersApplied = useMemo(() => {
    const allColumnsVisible = allPropertyNames.every((prop) =>
      visibleColumns.has(prop)
    );
    const allConditionsVisible = Array.from(allConditionIds).every((id) =>
      visibleConditions.has(id)
    );
    return !allColumnsVisible || !allConditionsVisible;
  }, [visibleColumns, visibleConditions, allPropertyNames, allConditionIds]);

  // Handler to clear all filters
  const handleClearFilters = () => {
    // Show all columns
    const defaultVisible = buildDefaultGridViewVisibleColumns(allPropertyNames);
    setVisibleColumns(defaultVisible);
    // Show all conditions
    setVisibleConditions(new Set(allConditionIds));
    setDifferenceFilterSignature(null);
    clearPersistedFilters(exp_id);
  };

  // Rehydrate applied filters from in-memory route-lifetime state. Because the
  // store is not persisted, a full browser refresh naturally resets filters.
  useEffect(() => {
    if (!conditions || conditions.length === 0) {
      return;
    }

    const nextFilters = reconcileGridViewFilters({
      savedFilters,
      allPropertyNames,
      allConditionIds,
    });

    setVisibleColumns(nextFilters.visibleColumns);
    setVisibleConditions(nextFilters.visibleConditions);
  }, [conditions, allPropertyNames, allConditionIds, savedFilters]);

  useEffect(() => {
    if (!conditions || conditions.length === 0) {
      return;
    }

    if (initializedBaselineRef.current) {
      return;
    }

    initializeBulkBaseline(conditions);
    initializedBaselineRef.current = true;
  }, [conditions, initializeBulkBaseline]);

  // Filter rows based on visible conditions
  const filteredRows = useMemo(() => {
    return baseRows.filter((row) =>
      visibleConditions.has(row.conditionId as number)
    );
  }, [baseRows, visibleConditions]);

  // Update rows when filteredRows or tracked bulk changes change
  useEffect(() => {
    setRows((prevRows) => {
      const sourceById = new Map<any, GridValidRowModel>();
      filteredRows.forEach((row) => sourceById.set(row.id, row));

      const preferredOrder = prevRows
        .map((row) => row.id)
        .filter((id) => sourceById.has(id));

      filteredRows.forEach((row) => {
        if (!preferredOrder.includes(row.id)) {
          preferredOrder.push(row.id);
        }
      });

      return preferredOrder.map((id) => {
        const baseRow = { ...sourceById.get(id) };
        const conditionId = baseRow.conditionId as number;

        Object.values(cellChangesByKey).forEach((change) => {
          if (change.conditionId === conditionId) {
            baseRow[change.field] = change.currentValue;
          }
        });

        return baseRow;
      });
    });
  }, [filteredRows, cellChangesByKey]);

  useEffect(() => {
    if (!editingCell) {
      return;
    }

    const stillExists = rows.some(
      (row) =>
        String(row.id) === String(editingCell.rowId) &&
        row[editingCell.field] !== undefined
    );

    if (!stillExists) {
      setEditingCell(null);
    }
  }, [editingCell, rows]);

  // Handle drag over to show drop indicator
  const handleDragOver = (event: DragEndEvent) => {
    const { over } = event;
    setOverId(over ? over.id : null);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setRows((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }

    setActiveId(null);
    setOverId(null);
  };

  const handleDragStart = (event: DragEndEvent) => {
    setActiveId(event.active.id);

    // If there's an active sort, persist the current sorted order before clearing
    if (sortModel.length > 0 && gridApiRef.current) {
      const sortedRowIds = gridApiRef.current.getSortedRowIds();
      const sortedRows = sortedRowIds
        .map((id: any) => rows.find((row) => row.id === id))
        .filter((row: any) => row !== undefined);
      setRows(sortedRows);
    }

    // Clear sorting when dragging starts
    setSortModel([]);
  };

  // For each (bomPath, typeKind, typeIdentity) location, compute how many
  // distinct instances exist across the union of conditions. Used to decide
  // whether headers need a "#N" disambiguator (only when there are 2+
  // instances at that location anywhere in the dataset, so the common
  // single-instance case keeps a clean header).
  //
  // (instance counts and per-item ordinals are computed in `gridIndex` above
  // in a single pass over the conditions tree.)

  // Build structured display parts for a column key. Adds "#N" only when 2+
  // instances exist at the same location, to keep single-instance columns
  // clean. The grid header (`buildColumnHeader`) and the filter modal both
  // consume this; the modal renders type/field on separate lines while the
  // grid header inlines them with a colon.
  const buildColumnDisplayInfo = useCallback(
    (
      field: string
    ): {
      typeName: string;
      fieldName: string;
      type: "Attribute" | "Parameter";
    } => {
      const parsed = parseColumnKey(field);
      if (!parsed) {
        return { typeName: field, fieldName: "", type: "Attribute" };
      }

      const typeName =
        parsed.typeKind === "P"
          ? processTypeDisplayNames.get(parsed.typeIdentity) ||
            parsed.typeIdentity
          : itemTypeDisplayNames.get(parsed.typeIdentity) ||
            parsed.typeIdentity;

      const displayFieldName = humanizeFieldName(parsed.fieldName);

      // Decide whether to add "#N" disambiguator.
      let suffix = "";
      if (parsed.typeKind === "P") {
        const key = `${parsed.bomPath}|P:${parsed.typeIdentity}`;
        const instanceSet = gridIndex.processInstances.get(key);
        if (instanceSet && instanceSet.size > 1) {
          // Position N = ordinal of THIS instancePath within the sorted set
          // of distinct instance paths at this location.
          const sorted = Array.from(instanceSet).sort((a, b) => {
            const av = instancePathOrdinals(a);
            const bv = instancePathOrdinals(b);
            for (let i = 0; i < Math.max(av.length, bv.length); i += 1) {
              const ai = av[i] ?? -1;
              const bi = bv[i] ?? -1;
              if (ai !== bi) return ai - bi;
            }
            // Same ordinals under different ancestor types: keep it stable.
            return a.localeCompare(b);
          });
          const ordinal = sorted.indexOf(parsed.instancePath);
          if (ordinal >= 0) {
            suffix = ` #${ordinal + 1}`;
          }
        }
      } else if (parsed.bomPath) {
        // Item: the ordinal-among-same-type-siblings is now embedded as the
        // trailing `<typeName>:<ordinal>` segment of `parts.bomPath`. Add a
        // `#N` suffix only when at least one condition has 2+ same-type
        // siblings at this parent path; the common single-instance case
        // keeps a clean header.
        const lastSep = parsed.bomPath.lastIndexOf(">");
        const parentBomPath =
          lastSep >= 0 ? parsed.bomPath.slice(0, lastSep) : "";
        const leafSegment =
          lastSep >= 0 ? parsed.bomPath.slice(lastSep + 1) : parsed.bomPath;
        const colonIdx = leafSegment.lastIndexOf(":");
        const ordinal =
          colonIdx >= 0 ? parseInt(leafSegment.slice(colonIdx + 1), 10) : NaN;
        const siblingKey = `${parentBomPath}|I:${parsed.typeIdentity}`;
        const siblingCount = gridIndex.itemSiblingCounts.get(siblingKey) ?? 0;
        if (siblingCount > 1 && !Number.isNaN(ordinal)) {
          suffix = ` #${ordinal + 1}`;
        }
      }

      return {
        typeName: `${typeName}${suffix}`,
        fieldName: displayFieldName,
        type: parsed.typeKind === "P" ? "Parameter" : "Attribute",
      };
    },
    [gridIndex, processTypeDisplayNames, itemTypeDisplayNames]
  );

  const buildColumnHeader = useCallback(
    (field: string): string => {
      const info = buildColumnDisplayInfo(field);
      return info.fieldName
        ? `${info.typeName}: ${info.fieldName}`
        : info.typeName;
    },
    [buildColumnDisplayInfo]
  );

  const getAttributeDescription = useCallback(
    (field: string): string => {
      const parsed = parseColumnKey(field);
      if (!parsed || parsed.typeKind !== "I") {
        return "";
      }

      for (const condition of conditions || []) {
        const resolved = resolveItemAtBomPath(condition, parsed.bomPath);
        const item = resolved?.item.item;
        if (!item || getItemTypeName(item) !== parsed.typeIdentity) {
          continue;
        }
        if (!("item_type" in item)) {
          continue;
        }

        const attribute = item.item_type?.attributes?.find(
          (attr) => attr.name === parsed.fieldName
        );
        const description = attribute?.description?.trim();
        if (description) {
          return description;
        }
      }

      return "";
    },
    [conditions]
  );

  const buildCopyColumnTooltipTitle = useCallback(
    (field: string, headerName: string): React.ReactNode => {
      const attributeDescription = getAttributeDescription(field);
      const copyHint = "(Ctrl+Click to copy column)";

      return (
        <Box>
          <Typography variant="body2">{headerName}</Typography>
          {attributeDescription && (
            <Typography variant="body2">{attributeDescription}</Typography>
          )}
          <Typography variant="body2">{copyHint}</Typography>
        </Box>
      );
    },
    [getAttributeDescription]
  );

  // Map of column field -> structured display parts, passed to
  // GridViewFilterModal so it does not need to re-derive instance ordinals
  // or humanize field names itself.
  const propertyDisplayInfo = useMemo(() => {
    const map = new Map<
      string,
      { typeName: string; fieldName: string; type: "Attribute" | "Parameter" }
    >();
    allPropertyNames.forEach((field) => {
      map.set(field, buildColumnDisplayInfo(field));
    });
    return map;
  }, [allPropertyNames, buildColumnDisplayInfo]);

  // Whether a condition is locked from bulk edits in GridView. A save in
  // flight (pendingSaveConditionIdSet) always locks the row; otherwise this
  // shares BOMView's lock rule (see `isConditionEditLocked`).
  const isConditionLocked = useCallback(
    (conditionId?: number): boolean => {
      if (typeof conditionId !== "number") {
        return false;
      }
      if (pendingSaveConditionIdSet.has(conditionId)) {
        return true;
      }
      return isConditionEditLocked(
        conditionId,
        activeModifyConditionId,
        submittedConditionIds
      );
    },
    [submittedConditionIds, pendingSaveConditionIdSet, activeModifyConditionId]
  );

  const isRowFieldEditable = useCallback(
    (row: GridValidRowModel, field: string): boolean => {
      if (field !== "conditionName" && !isEditableGridField(field)) {
        return false;
      }

      // Lock edits on rows whose condition is not editable in the current
      // context (submitted, mid-save, or outside the actively-modified work
      // order group). The bulk-edit grid is not the surface for changing
      // committed conditions; modifications happen via the dedicated work
      // order modification flow started in StatusView.
      const conditionId = row?.conditionId as number | undefined;
      if (isConditionLocked(conditionId)) {
        return false;
      }

      if (field === "conditionName") {
        return true;
      }

      // Honor template `attribute_lock` / `parameter_lock` constraints.
      // While constraints are still loading for this row's condition,
      // `_locked_*` is true (pessimistic) so cells are read-only until we
      // know the real lock state.
      if (row?.[`_locked_${field}`]) {
        return false;
      }

      return Boolean(row?.[`_has_${field}`]);
    },
    [isConditionLocked]
  );

  const commitConditionNameEdit = useCallback(
    (
      rowId: string | number,
      rawValue: ManualEditInput,
      rowSnapshot?: GridValidRowModel
    ): boolean => {
      const row =
        rowSnapshot ||
        rows.find((existingRow) => String(existingRow.id) === String(rowId));
      if (!row) {
        return false;
      }

      const conditionId = row.conditionId as number | undefined;
      if (typeof conditionId !== "number" || isConditionLocked(conditionId)) {
        return false;
      }

      const coercion = coerceConditionName(rawValue);
      if (!coercion.ok) {
        setErrorMessage(
          `Invalid value for Condition Name; ${coercion.reason}. Change was not applied.`
        );
        return false;
      }
      const coercedValue = coercion.value;

      const changeKey = `${conditionId}::conditionName`;
      const existingChange = cellChangesByKey[changeKey];
      const previousValue = existingChange
        ? existingChange.previousValue
        : String((row._condition as FormspecCondition | undefined)?.name || "");
      const currentEffectiveValue = existingChange
        ? existingChange.currentValue
        : String(row.conditionName || previousValue || "");

      if (areValuesEqual(currentEffectiveValue, coercedValue)) {
        return true;
      }

      setRecentlyEditedCells(new Set([`${rowId}::conditionName`]));
      setRows((prevRows) =>
        prevRows.map((existingRow) =>
          String(existingRow.id) === String(rowId)
            ? {
                ...existingRow,
                conditionName: coercedValue,
              }
            : existingRow
        )
      );

      trackBulkCellChange({
        conditionId,
        conditionName: String(coercedValue),
        field: "conditionName",
        columnHeader: "Condition Name",
        target: { type: "condition", field: "name" },
        previousValue,
        currentValue: coercedValue,
        previousDisplay: formatValue(previousValue),
        currentDisplay: formatValue(coercedValue),
      });

      return true;
    },
    [
      rows,
      cellChangesByKey,
      setErrorMessage,
      isConditionLocked,
      trackBulkCellChange,
    ]
  );

  const allColumns: GridColDef[] = useMemo(() => {
    // Helper function to copy column data
    const copyColumnToClipboard = (field: string, headerName: string) => {
      const columnHeader = headerName || field;
      const columnValues = rows
        .map((row) => formatValue(row[field] as ValueType | null))
        .join("\n");
      navigator.clipboard.writeText(`${columnHeader}\n${columnValues}`);
      setCopyToastMessage(`Copied ${columnHeader}`);
    };

    const renderCopyableColumnHeader = (params: GridColumnHeaderParams) => {
      const headerName = params.colDef.headerName || params.field;

      return (
        <Tooltip
          title={buildCopyColumnTooltipTitle(params.field, headerName)}
          arrow
        >
          <span
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                e.stopPropagation();
                copyColumnToClipboard(params.field, headerName);
              }
            }}
            style={{ cursor: "pointer" }}
            onMouseEnter={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.currentTarget.style.textDecoration = "underline";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
          >
            {headerName}
          </span>
        </Tooltip>
      );
    };

    const baseColumns: GridColDef[] = [
      {
        field: "dragHandle",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams) => {
          return <DragHandleCell rowId={params.row.id} />;
        },
      },
      {
        field: "conditionId",
        headerName: "Condition ID",
        width: 120,
        editable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              height: "100%",
              justifyContent: "flex-start",
              minWidth: 0,
              textAlign: "left",
              width: "100%",
            }}
          >
            {formatValue(params.value)}
          </Box>
        ),
        renderHeader: renderCopyableColumnHeader,
      },
      {
        field: "conditionName",
        headerName: "Condition Name",
        width: 200,
        editable: false,
        renderHeader: renderCopyableColumnHeader,
        renderCell: (params: GridRenderCellParams) => {
          const conditionId = params.row.conditionId as number;
          const hasViolations =
            violationsByConditionId.get(conditionId) ?? false;
          const isPendingSave = pendingSaveConditionIdSet.has(conditionId);
          // Show the "not editable" indicator only when the condition is
          // actually locked in the current context. A condition belonging to
          // the actively-modified work order group is editable here, so it
          // should not surface the submitted/locked icon. Exclude the pending
          // save case since it renders its own spinner below.
          const isLockedCondition =
            isConditionLocked(conditionId) && !isPendingSave;
          const isSelectedThisCell =
            activeCell !== null &&
            String(activeCell.rowId) === String(params.id) &&
            activeCell.field === "conditionName";
          const isEditingThisCell =
            editingCell !== null &&
            String(editingCell.rowId) === String(params.id) &&
            editingCell.field === "conditionName";
          const canEditThisCell = isRowFieldEditable(
            params.row as GridValidRowModel,
            "conditionName"
          );
          const {
            pathname,
            searchParams: currentSearchParams,
            checkUnsavedChangesBeforeNavigation: currentCheckUnsavedChanges,
          } = linkContextRef.current;

          if (isEditingThisCell) {
            return (
              <InlineEditCell
                initialValue={params.value as ValueType | null | undefined}
                seedValue={editingCell?.initialValue}
                columnMeta={{
                  headerName: "Condition Name",
                  type: "string",
                  kind: "text",
                  enumValues: [],
                }}
                onCommit={(nextValue) => {
                  commitConditionNameEdit(
                    params.id,
                    nextValue,
                    params.row as GridValidRowModel
                  );
                  setEditingCell(null);
                }}
                onCancel={() => setEditingCell(null)}
              />
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                height: "100%",
                minWidth: 0,
                textAlign: "left",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flex: "1 1 auto",
                  gap: 0.75,
                  minWidth: 0,
                }}
              >
                {isLockedCondition && (
                  <Tooltip
                    title="This condition isn't editable because it's already been submitted"
                    placement="top"
                    arrow
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <Send
                        style={{
                          fontSize: "16px",
                          color: "#6b6b6b",
                        }}
                      />
                    </span>
                  </Tooltip>
                )}
                {isPendingSave && (
                  <Tooltip title="Saving changes..." placement="top" arrow>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress size={16} />
                    </span>
                  </Tooltip>
                )}
                <ConditionNameLink
                  name={params.value as string}
                  to={createConditionLink(
                    pathname,
                    currentSearchParams,
                    conditionId
                  )}
                  hasViolations={hasViolations}
                  onClick={(e) => e.stopPropagation()}
                  onBeforeNavigate={currentCheckUnsavedChanges}
                />
              </Box>
              {isSelectedThisCell && canEditThisCell && (
                <Box
                  component="button"
                  type="button"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setEditingCell({
                      rowId: params.id,
                      field: "conditionName",
                    });
                    if (recentlyEditedCells.size > 0) {
                      setRecentlyEditedCells(new Set());
                    }
                  }}
                  sx={{
                    alignItems: "center",
                    background: "transparent",
                    border: 0,
                    color: "#1976d2",
                    cursor: "pointer",
                    display: "inline-flex",
                    flex: "0 0 auto",
                    padding: 0,
                  }}
                  aria-label="Edit condition name"
                >
                  <Pencil sx={{ fontSize: 16 }} />
                </Box>
              )}
            </Box>
          );
        },
      },
    ];

    // Add columns for each attribute
    const attributeColumns: GridColDef[] = allPropertyNames
      .map((attrName) => {
        // Determine column type based on first non-null value across all conditions
        let columnType: "string" | "number" | "boolean" = "string";

        for (const condition of conditions || []) {
          const value = getNestedFieldValue(condition, attrName);
          if (value !== null) {
            if (typeof value === "number") {
              columnType = "number";
            } else if (typeof value === "boolean") {
              columnType = "boolean";
            }
            break;
          }
        }

        const headerName = buildColumnHeader(attrName);

        return {
          field: attrName,
          headerName: headerName,
          width: 180,
          editable: false,
          type: columnType,
          renderHeader: renderCopyableColumnHeader,
          renderCell: (params: GridRenderCellParams) => {
            const hasAttr = params.row[`_has_${attrName}`];
            const isSelectedThisCell =
              activeCell !== null &&
              String(activeCell.rowId) === String(params.id) &&
              activeCell.field === attrName;
            const isEditingThisCell =
              editingCell !== null &&
              String(editingCell.rowId) === String(params.id) &&
              editingCell.field === attrName;
            const canEditThisCell = isRowFieldEditable(
              params.row as GridValidRowModel,
              attrName
            );

            if (isEditingThisCell && hasAttr) {
              const fallbackKind: ManualEditColumnMeta["kind"] = isRangeValue(
                params.value
              )
                ? "range"
                : columnType === "number"
                ? "number"
                : columnType === "boolean"
                ? "boolean"
                : "text";
              const columnMeta = columnMetaByFieldRef.current[attrName] || {
                headerName,
                type: columnType,
                kind: fallbackKind,
                enumValues: [],
              };

              return (
                <InlineEditCell
                  initialValue={params.value as ValueType | null | undefined}
                  seedValue={editingCell?.initialValue}
                  columnMeta={columnMeta}
                  onCommit={(nextValue) => {
                    commitManualEditRef.current(
                      params.id,
                      attrName,
                      nextValue,
                      params.row as GridValidRowModel
                    );

                    setEditingCell(null);
                  }}
                  onCancel={() => setEditingCell(null)}
                />
              );
            }

            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  height: "100%",
                  minWidth: 0,
                  width: "100%",
                  color: hasAttr ? "inherit" : "text.disabled",
                  fontStyle: hasAttr ? "normal" : "italic",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    overflowWrap: "anywhere",
                    textAlign: "left",
                    whiteSpace: "normal",
                  }}
                >
                  {formatValue(params.value)}
                </Box>
                {isSelectedThisCell && canEditThisCell && (
                  <Box
                    component="button"
                    type="button"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setEditingCell({
                        rowId: params.id,
                        field: attrName,
                      });
                      if (recentlyEditedCells.size > 0) {
                        setRecentlyEditedCells(new Set());
                      }
                      if (pasteErrorCells.size > 0) {
                        setPasteErrorCells(new Set());
                      }
                    }}
                    sx={{
                      alignItems: "center",
                      background: "transparent",
                      border: 0,
                      color: "#1976d2",
                      cursor: "pointer",
                      display: "inline-flex",
                      flex: "0 0 auto",
                      padding: 0,
                    }}
                    aria-label="Edit cell"
                  >
                    <Pencil sx={{ fontSize: 16 }} />
                  </Box>
                )}
              </Box>
            );
          },
        };
      })
      .sort((a, b) => a.headerName.localeCompare(b.headerName));

    return [...baseColumns, ...attributeColumns];
  }, [
    allPropertyNames,
    conditions,
    buildColumnHeader,
    buildCopyColumnTooltipTitle,
    rows,
    activeCell,
    editingCell,
    commitConditionNameEdit,
    violationsByConditionId,
    isConditionLocked,
    pendingSaveConditionIdSet,
    getNestedFieldValue,
    isRowFieldEditable,
    recentlyEditedCells.size,
    pasteErrorCells.size,
  ]);

  // Filter columns based on visibility
  const columns: GridColDef[] = useMemo(() => {
    return allColumns.filter(
      (col) =>
        col.field === "dragHandle" ||
        col.field === "conditionId" ||
        col.field === "conditionName" ||
        visibleColumns.has(col.field)
    );
  }, [allColumns, visibleColumns]);

  useEffect(() => {
    const meta: Record<string, ManualEditColumnMeta> = {};

    // Resolve the schema (data_type / enum config) for the EXACT instance the
    // column key points at. Walking the schema by parsed-key path keeps
    // process_type metadata aligned with the specific instance the user is
    // editing, even when multiple same-type instances live at the same
    // location.
    const collectFieldMetadata = (
      condition: FormspecCondition,
      targetField: string
    ): {
      dataType: string | null;
      enumValues: string[];
      isMultiEnum: boolean;
    } | null => {
      const parsed = parseColumnKey(targetField);
      if (!parsed) return null;

      const resolvedOwner = resolveItemAtBomPath(condition, parsed.bomPath);
      if (!resolvedOwner) return null;
      const owningItem = resolvedOwner.item;

      if (parsed.typeKind === "I") {
        const item = owningItem.item;
        if (!item || getItemTypeName(item) !== parsed.typeIdentity) return null;
        if (!("item_type" in item)) return null;
        const attribute = item.item_type?.attributes?.find(
          (attr) => attr.name === parsed.fieldName
        );
        if (!attribute) return null;
        return {
          dataType: attribute.data_type || null,
          enumValues: getEnumValuesFromConfig(attribute.config),
          isMultiEnum: isMultiSelectEnumDataType(attribute.data_type),
        };
      }

      const resolved = resolveProcessByInstancePath(
        owningItem.processes,
        parsed.typeIdentity,
        parsed.instancePath
      );
      if (!resolved) return null;
      const parameter = (
        resolved.entry.process.process_type?.parameters || []
      ).find((param) => param.name === parsed.fieldName);
      if (!parameter) return null;
      return {
        dataType: parameter.data_type || null,
        enumValues: getEnumValuesFromConfig(parameter.config),
        isMultiEnum: isMultiSelectEnumDataType(parameter.data_type),
      };
    };

    allColumns.forEach((column) => {
      if (!column.field || !isEditableGridField(column.field)) {
        return;
      }

      let kind: ManualEditColumnMeta["kind"] =
        (column.type || "string") === "number"
          ? "number"
          : (column.type || "string") === "boolean"
          ? "boolean"
          : "text";
      let enumValues: string[] = [];

      for (const condition of conditions || []) {
        const fieldMeta = collectFieldMetadata(condition, column.field);
        if (!fieldMeta) {
          continue;
        }

        const upperDataType = fieldMeta.dataType?.toUpperCase();

        if (
          isEnumDataType(fieldMeta.dataType) &&
          fieldMeta.enumValues.length > 0
        ) {
          enumValues = fieldMeta.enumValues;
          kind = fieldMeta.isMultiEnum ? "enum_multi" : "enum_single";
          break;
        }

        if (upperDataType === "RANGE") {
          kind = "range";
          break;
        }
      }

      meta[column.field] = {
        headerName: column.headerName || column.field,
        type: (column.type || "string") as "string" | "number" | "boolean",
        kind,
        enumValues,
      };
    });
    columnMetaByFieldRef.current = meta;
    setColumnMetaByField(meta);
  }, [allColumns, conditions]);

  const handleApplyColumns = (
    newVisibleColumns: Set<string>,
    newVisibleConditions: Set<number>
  ) => {
    setVisibleColumns(newVisibleColumns);
    setVisibleConditions(newVisibleConditions);
    setDifferenceFilterSignature(null);
    persistFilters(exp_id, newVisibleColumns, newVisibleConditions);
  };

  const currentDifferenceFilterSignature = useMemo(
    () =>
      getDifferenceFilterSignature(
        rows,
        allPropertyNames,
        visibleConditions,
        visibleColumns
      ),
    [rows, allPropertyNames, visibleConditions, visibleColumns]
  );

  const isViewDifferencesDisabled =
    rows.length < 2 ||
    !isGetConditionsSuccess ||
    differenceFilterSignature === currentDifferenceFilterSignature;

  const handleViewDifferences = () => {
    if (rows.length < 2) {
      return;
    }

    const nextVisibleColumns = getDifferenceVisibleColumns(
      rows,
      allPropertyNames
    );
    const nextSignature = getDifferenceFilterSignature(
      rows,
      allPropertyNames,
      visibleConditions,
      nextVisibleColumns
    );

    setVisibleColumns(nextVisibleColumns);
    setDifferenceFilterSignature(nextSignature);
    setActiveCell(null);
    setEditingCell(null);
  };

  // Scroll to left when visible columns change
  useEffect(() => {
    if (gridContainerRef.current) {
      const scrollableElement = gridContainerRef.current.querySelector(
        ".MuiDataGrid-virtualScroller"
      );
      if (scrollableElement) {
        scrollableElement.scrollLeft = 0;
      }
    }

    const animationFrameId = requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });
    return () => cancelAnimationFrame(animationFrameId);
  }, [visibleColumns]);

  const handleCancelColumns = () => {
    setFilterModalOpen(false);
  };

  const isCellEditable = useCallback(
    (params: GridCellParams) => {
      if (
        params.field !== "conditionName" &&
        !isEditableGridField(params.field)
      ) {
        return false;
      }

      return isRowFieldEditable(params.row as GridValidRowModel, params.field);
    },
    [isRowFieldEditable]
  );

  const commitManualEdit = useCallback(
    (
      rowId: string | number,
      field: string,
      rawValue: ManualEditInput,
      rowSnapshot?: GridValidRowModel
    ): boolean => {
      const row =
        rowSnapshot ||
        rows.find((existingRow) => String(existingRow.id) === String(rowId));
      if (!row) {
        return false;
      }

      if (!isEditableGridField(field) || !row[`_has_${field}`]) {
        return false;
      }

      // Defense-in-depth: never commit to a locked cell, even if a paste or
      // programmatic edit slips past the cell-level gate.
      if (row[`_locked_${field}`]) {
        return false;
      }

      // Defense-in-depth: even if a paste/programmatic commit slips past the
      // cell-level gate, never write to a row whose condition is locked in the
      // current context (submitted, mid-save, or outside the actively-modified
      // work order group).
      const lockedConditionId = row.conditionId as number | undefined;
      if (isConditionLocked(lockedConditionId)) {
        return false;
      }

      const columnMeta = columnMetaByField[field] || null;
      const fallbackMeta: ManualEditColumnMeta = {
        headerName: columnMeta?.headerName || field,
        type: columnMeta?.type || "string",
        kind: columnMeta?.kind || "text",
        enumValues: columnMeta?.enumValues || [],
      };

      const coercion = coerceManualValue(rawValue, fallbackMeta);
      if (!coercion.ok) {
        const headerLabel = columnMeta?.headerName || field;
        setErrorMessage(
          `Invalid value for ${headerLabel}; ${coercion.reason}. Change was not applied.`
        );
        return false;
      }
      const coercedValue = coercion.value;

      setRecentlyEditedCells(new Set([`${rowId}::${field}`]));

      const target = resolveNestedFieldTarget(
        row._condition as ItemWithBOM,
        field
      );

      if (!target) {
        return false;
      }

      const conditionId = row.conditionId as number;
      const changeKey = `${conditionId}::${field}`;
      const existingChange = cellChangesByKey[changeKey];
      const previousValue = existingChange
        ? existingChange.previousValue
        : (getNestedFieldValue(
            row._condition as ItemWithBOM,
            field
          ) as ValueType | null);

      const currentEffectiveValue = existingChange
        ? existingChange.currentValue
        : (row[field] as ValueType | null | undefined) ?? previousValue;

      if (areValuesEqual(currentEffectiveValue, coercedValue)) {
        return true;
      }

      setRows((prevRows) =>
        prevRows.map((existingRow) =>
          String(existingRow.id) === String(rowId)
            ? {
                ...existingRow,
                [field]: coercedValue,
              }
            : existingRow
        )
      );

      trackBulkCellChange({
        conditionId,
        conditionName: String(row.conditionName || ""),
        field,
        columnHeader: columnMeta?.headerName || field,
        target,
        previousValue,
        currentValue: coercedValue,
        previousDisplay: formatValue(previousValue),
        currentDisplay: formatValue(coercedValue),
      });

      return true;
    },
    [
      rows,
      columnMetaByField,
      cellChangesByKey,
      setErrorMessage,
      setRecentlyEditedCells,
      trackBulkCellChange,
      isConditionLocked,
      getNestedFieldValue,
      resolveNestedFieldTarget,
    ]
  );

  useEffect(() => {
    commitManualEditRef.current = commitManualEdit;
  }, [commitManualEdit]);

  const getOrderedRows = (): GridValidRowModel[] => {
    if (sortModel.length > 0 && gridApiRef.current?.getSortedRowIds) {
      const sortedRowIds = gridApiRef.current.getSortedRowIds();
      return sortedRowIds
        .map((id: string | number) => rows.find((row) => row.id === id))
        .filter(
          (row: GridValidRowModel | undefined): row is GridValidRowModel =>
            row !== undefined
        );
    }

    return rows;
  };

  const handleDownloadCsv = () => {
    const exportColumns = columns.filter(
      (column) => column.field && column.field !== "dragHandle"
    );
    const orderedRows = getOrderedRows();

    if (exportColumns.length === 0 || orderedRows.length === 0) {
      return;
    }

    const csvRows = [
      exportColumns.map((column) => column.headerName || column.field),
      ...orderedRows.map((row) =>
        exportColumns.map((column) =>
          formatValue(row[column.field] as ValueType | null | undefined)
        )
      ),
    ];
    const csv = csvRows
      .map((row) => row.map(escapeCsvCell).join(","))
      .join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `EXP${exp_id || ""}_conditions.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (!activeCell) {
      return;
    }

    const pastedText = event.clipboardData.getData("text/plain");
    if (!pastedText) {
      return;
    }

    const matrix = parseClipboardMatrix(pastedText);
    if (matrix.length === 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Paste geometry: use all visible data columns (excluding structural
    // columns like dragHandle / conditionId). This preserves
    // the user's spatial intuition: the Nth clipboard column maps to the Nth
    // visible data column from the active cell, even if some columns in
    // between are not editable. Non-editable cells are skipped with a warning
    // rather than collapsed away.
    const pasteTargetColumns = columns.filter(
      (col) =>
        col.field &&
        (col.field === "conditionName" ||
          !STRUCTURAL_GRID_FIELDS.has(col.field))
    );
    const startField =
      activeCell.field === "__check__" || activeCell.field === "dragHandle"
        ? "conditionId"
        : activeCell.field;

    const orderedRows = getOrderedRows();
    const startRowIndex = orderedRows.findIndex(
      (row) => row.id === activeCell.rowId
    );
    const startColumnIndex = pasteTargetColumns.findIndex(
      (column) => column.field === startField
    );

    if (startRowIndex < 0 || startColumnIndex < 0) {
      return;
    }

    let truncatedCells = 0;
    let layoutSkippedCells = 0;
    let invalidCells = 0;
    const invalidDetails: string[] = [];
    const appliedCellKeys = new Set<string>();
    const errorCellKeys = new Set<string>();

    const nextRows = rows.map((row) => ({ ...row }));
    const rowIndexById = new Map<any, number>();
    nextRows.forEach((row, index) => rowIndexById.set(row.id, index));

    for (let rowOffset = 0; rowOffset < matrix.length; rowOffset += 1) {
      const values = matrix[rowOffset];

      for (let colOffset = 0; colOffset < values.length; colOffset += 1) {
        const targetOrderedRowIndex = startRowIndex + rowOffset;
        const targetColumnIndex = startColumnIndex + colOffset;

        const rowOutOfBounds = targetOrderedRowIndex >= orderedRows.length;
        const colOutOfBounds = targetColumnIndex >= pasteTargetColumns.length;
        if (rowOutOfBounds || colOutOfBounds) {
          truncatedCells += 1;
          continue;
        }

        const orderedTargetRow = orderedRows[targetOrderedRowIndex];
        const targetRowIndex = rowIndexById.get(orderedTargetRow.id);
        if (targetRowIndex == null) {
          truncatedCells += 1;
          continue;
        }

        const targetRow = nextRows[targetRowIndex];
        const targetColumn = pasteTargetColumns[targetColumnIndex];
        const isConditionNameField = targetColumn.field === "conditionName";

        // Skip cells whose target column is non-editable globally, or whose
        // row doesn't have an editable cell at this column, or whose row
        // belongs to an already-submitted condition.
        // For conditionName (structural column), only check submitted status.
        // For other fields, check editability + lock status + submitted status.
        const targetConditionId = targetRow.conditionId as number | undefined;
        if (!isEditableGridField(targetColumn.field)) {
          if (!STRUCTURAL_GRID_FIELDS.has(targetColumn.field)) {
            layoutSkippedCells += 1;
          }
          continue;
        }

        if (
          (isConditionNameField && typeof targetConditionId !== "number") ||
          (!isConditionNameField && !isEditableGridField(targetColumn.field)) ||
          (!isConditionNameField && !targetRow[`_has_${targetColumn.field}`]) ||
          (!isConditionNameField &&
            targetRow[`_locked_${targetColumn.field}`]) ||
          isConditionLocked(
            typeof targetConditionId === "number"
              ? targetConditionId
              : undefined
          )
        ) {
          layoutSkippedCells += 1;
          continue;
        }

        if (isConditionNameField) {
          if (typeof targetConditionId !== "number") {
            layoutSkippedCells += 1;
            continue;
          }

          const coercion = coerceConditionName(values[colOffset]);
          if (!coercion.ok) {
            invalidCells += 1;
            if (invalidDetails.length < 5) {
              const conditionLabel = String(
                targetRow.conditionName || targetRow.conditionId
              );
              invalidDetails.push(
                `Condition ${conditionLabel}, Condition Name: "${values[colOffset]}" ${coercion.reason}`
              );
            }
            continue;
          }
          const coercedValue = coercion.value;

          appliedCellKeys.add(`${targetRow.id}::conditionName`);

          const currentValue = String(targetRow.conditionName || "");
          if (areValuesEqual(currentValue, coercedValue)) {
            continue;
          }

          targetRow.conditionName = coercedValue;

          const changeKey = `${targetConditionId}::conditionName`;
          const existingChange = cellChangesByKey[changeKey];
          const previousValue = existingChange
            ? existingChange.previousValue
            : String(
                (targetRow._condition as FormspecCondition | undefined)?.name ||
                  ""
              );

          trackBulkCellChange({
            conditionId: targetConditionId,
            conditionName: String(coercedValue),
            field: "conditionName",
            columnHeader: "Condition Name",
            target: { type: "condition", field: "name" },
            previousValue,
            currentValue: coercedValue,
            previousDisplay: formatValue(previousValue),
            currentDisplay: formatValue(coercedValue),
          });
          continue;
        }

        const condition = targetRow._condition as ItemWithBOM;
        const target = resolveNestedFieldTarget(condition, targetColumn.field);

        if (!target) {
          layoutSkippedCells += 1;
          continue;
        }

        const columnMeta = columnMetaByField[targetColumn.field];
        const fallbackMeta: ManualEditColumnMeta = {
          headerName:
            columnMeta?.headerName ||
            targetColumn.headerName ||
            targetColumn.field,
          type: columnMeta?.type || "string",
          kind: columnMeta?.kind || "text",
          enumValues: columnMeta?.enumValues || [],
        };
        const columnHeader = fallbackMeta.headerName;

        const coercion = coerceManualValue(values[colOffset], fallbackMeta);
        if (!coercion.ok) {
          invalidCells += 1;
          errorCellKeys.add(`${targetRow.id}::${targetColumn.field}`);
          if (invalidDetails.length < 5) {
            const conditionLabel = String(
              targetRow.conditionName || targetRow.conditionId
            );
            invalidDetails.push(
              `Condition ${conditionLabel}, ${columnHeader}: "${values[colOffset]}" ${coercion.reason}`
            );
          }
          continue;
        }
        const coercedValue = coercion.value;

        appliedCellKeys.add(`${targetRow.id}::${targetColumn.field}`);

        const currentValue = targetRow[targetColumn.field] as ValueType | null;
        if (areValuesEqual(currentValue, coercedValue)) {
          continue;
        }

        targetRow[targetColumn.field] = coercedValue;

        const changeKey = `${targetRow.conditionId as number}::${
          targetColumn.field
        }`;
        const existingChange = cellChangesByKey[changeKey];
        const previousValue = existingChange
          ? existingChange.previousValue
          : (getNestedFieldValue(
              targetRow._condition as ItemWithBOM,
              targetColumn.field
            ) as ValueType | null);

        trackBulkCellChange({
          conditionId: targetRow.conditionId as number,
          conditionName: String(targetRow.conditionName || ""),
          field: targetColumn.field,
          columnHeader,
          target,
          previousValue,
          currentValue: coercedValue,
          previousDisplay: formatValue(previousValue),
          currentDisplay: formatValue(coercedValue),
        });
      }
    }

    setRows(nextRows);
    setRecentlyEditedCells(appliedCellKeys);
    setPasteErrorCells(errorCellKeys);

    if (truncatedCells > 0 || layoutSkippedCells > 0 || invalidCells > 0) {
      const messages = [];
      if (truncatedCells > 0) {
        messages.push(`${truncatedCells} value(s) truncated by grid bounds`);
      }
      if (layoutSkippedCells > 0) {
        messages.push(
          `${layoutSkippedCells} value(s) skipped because the target columns are not editable`
        );
      }
      if (invalidCells > 0) {
        messages.push(`${invalidCells} value(s) skipped for invalid values`);
      }
      const warningLines = messages.map((message) => `- ${message}`);
      const detailLines = invalidDetails.map((detail) => `- ${detail}`);
      if (invalidCells > invalidDetails.length) {
        detailLines.push("- ...");
      }

      const detailsSection =
        detailLines.length > 0 ? `\n\nDetails:\n${detailLines.join("\n")}` : "";

      setErrorMessage(
        `Paste completed with warnings:\n${warningLines.join(
          "\n"
        )}${detailsSection}`
      );
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <Container>
        <HeaderContainer>
          <Typography variant="h6">Compare Conditions</Typography>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Tooltip
              title={
                !isGetConditionsSuccess
                  ? "Loading conditions…"
                  : rows.length < 2
                  ? "Select at least two visible conditions to view differences"
                  : isViewDifferencesDisabled
                  ? "Identical columns are already hidden"
                  : "Hide columns with identical values across visible conditions"
              }
              arrow
            >
              <span>
                <Chip
                  clickable
                  disabled={isViewDifferencesDisabled}
                  icon={<DiffIcon style={{ color: "darkgray" }} />}
                  onClick={handleViewDifferences}
                  label="View Differences"
                />
              </span>
            </Tooltip>
            <Chip
              clickable
              className={hasFiltersApplied ? "active" : ""}
              onClick={() => setFilterModalOpen(true)}
              onDelete={hasFiltersApplied ? handleClearFilters : undefined}
              icon={<FilterIcon />}
              label="Filter"
            />
            <Chip
              clickable
              disabled={rows.length === 0}
              onClick={handleDownloadCsv}
              icon={<DownloadIcon />}
              label=""
              aria-label="Download CSV"
              title="Download CSV"
              sx={{
                width: 32,
                "& .MuiChip-icon": {
                  marginLeft: 0,
                  marginRight: 0,
                },
                "& .MuiChip-label": {
                  display: "none",
                  padding: 0,
                },
              }}
            />
          </Box>
        </HeaderContainer>

        <GridContent
          rows={rows}
          columns={columns}
          gridApiRef={gridApiRef}
          gridContainerRef={gridContainerRef}
          sortModel={sortModel}
          setSortModel={setSortModel}
          loading={!isGetConditionsSuccess}
          activeId={activeId}
          overId={overId}
          activeCell={activeCell}
          recentlyEditedCells={recentlyEditedCells}
          setRecentlyEditedCells={setRecentlyEditedCells}
          pasteErrorCells={pasteErrorCells}
          setPasteErrorCells={setPasteErrorCells}
          setActiveCell={setActiveCell}
          isRowFieldEditable={isRowFieldEditable}
          setEditingCell={setEditingCell}
          isCellEditable={isCellEditable}
          handlePaste={handlePaste}
          onCopySuccess={setCopyToastMessage}
        />

        {copyToastMessage && (
          <Alert
            open
            severity="success"
            onClose={() => setCopyToastMessage(null)}
          >
            {copyToastMessage}
          </Alert>
        )}

        <GridViewFilterModal
          open={filterModalOpen}
          onClose={handleCancelColumns}
          onApply={handleApplyColumns}
          allPropertyNames={allPropertyNames}
          propertyDisplayInfo={propertyDisplayInfo}
          initialVisibleColumns={visibleColumns}
          conditions={
            conditions?.map((c) => ({
              id: c.cell_condition_id!,
              name: c.name || `Condition ${c.cell_condition_id}`,
            })) || []
          }
          initialVisibleConditions={visibleConditions}
        />
      </Container>
    </DndContext>
  );
};

export default GridView;
